import type { CallsClient } from '@wtfalch/calls';
import type { ChatClient } from '@wtfalch/chat';

import type { ChatTimelineEvent } from '../chat/types.ts';
import { parseCallNotice } from './callNotice.ts';

export type IncomingCall = {
  roomId: string;
  roomName: string;
  sessionId: string;
  callerName: string;
};

export type Ringer = { start(): void; stop(): void };

/** How long a call rings before it gives up on its own. */
export const RING_TIMEOUT_MS = 30_000;
/** How often a ringing call is re-checked, to stop when it has ended. */
const END_POLL_MS = 5_000;

const JOINED_EVENT = 'agora:call-joined';

/** Tells the ringing watcher this person has joined the call in `roomId`. */
export function announceCallJoined(roomId: string): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(JOINED_EVENT, { detail: roomId }));
  }
}

/** A short two-tone ring through the Web Audio API, repeated until stopped.
 *  A no-op where there is no `AudioContext` or the browser refuses to start it. */
export function createRinger(): Ringer {
  let ctx: AudioContext | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  const beep = () => {
    if (!ctx) return;
    for (const [offset, freq] of [
      [0, 880],
      [0.25, 660],
    ] as const) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.15, ctx.currentTime + offset);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.22);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + offset);
      osc.stop(ctx.currentTime + offset + 0.25);
    }
  };
  return {
    start() {
      if (timer) return;
      try {
        ctx ??= new AudioContext();
        void ctx.resume();
      } catch {
        return;
      }
      beep();
      timer = setInterval(beep, 2000);
    },
    stop() {
      if (timer) clearInterval(timer);
      timer = null;
      void ctx?.close().catch(() => {});
      ctx = null;
    },
  };
}

/**
 * Rings for a call another member starts in a room. A call notice
 * (`callNotice.ts`) in a live timeline is only a hint: the call rings once the
 * calls service says that room has a live call under that same session id.
 * One ring per session id, ever: a re-render, a reconnect or a repeated notice
 * does not ring again. The ring stops when the call ends (polled), this person
 * joins (`announceCallJoined`) or `dismiss` is called (decline, answer), or
 * after `RING_TIMEOUT_MS`. Your own notices never ring, which covers your
 * other devices too (same user id). History from before the watcher started,
 * and muted rooms, never ring.
 *
 * Declining is a device-local `dismiss` plus a best-effort
 * `CallsClient.decline` to the service (agora#487): the poll below also
 * re-checks `invitation()` for this same reason, so a decline recorded from
 * one device still stops the others ringing, the same way `roomStatus`
 * already stops them when the call itself ends.
 */
export function watchIncomingCalls(
  chat: ChatClient,
  calls: Pick<CallsClient, 'roomStatus' | 'invitation'>,
  options: {
    ringer: Ringer;
    onChange: (incoming: IncomingCall[]) => void;
    /** Called once per call that starts ringing, e.g. for an OS notification. */
    onRing?: (call: IncomingCall) => void;
    now?: () => number;
  },
): { stop: () => void; dismiss: (roomId: string) => void } {
  let live = true;
  // `event.ts` is the server's own clock; `Date.now()` is this device's,
  // and the two can disagree by more than this watcher's own timeout
  // (agora#392) -- a client clock running 30s+ ahead of the server made
  // every notice in that window read as older than `startedAt` and get
  // skipped as history, dropping a call nobody ever got to ring for. The
  // newest timestamp already synced into any room, as of right now, is a
  // server-clock reading that costs nothing extra to take; `options.now`
  // (defaulting to the device clock) is only the fallback for an account
  // with no history anywhere yet to read one off.
  const newestSyncedServerTs = () => {
    let max = -Infinity;
    for (const room of chat.rooms()) {
      for (const event of chat.timeline(room.id).events) {
        if (event.ts > max) max = event.ts;
      }
    }
    return max;
  };
  const serverTs = newestSyncedServerTs();
  const startedAt = serverTs > -Infinity ? serverTs : (options.now ?? Date.now)();
  const seen = new Set<string>();
  const ringing = new Map<string, { call: IncomingCall; timers: (() => void)[] }>();
  const stopByRoom = new Map<string, () => void>();

  const publish = () => options.onChange([...ringing.values()].map((r) => r.call));

  const dismiss = (roomId: string) => {
    const entry = ringing.get(roomId);
    if (!entry) return;
    for (const cancel of entry.timers) cancel();
    ringing.delete(roomId);
    if (ringing.size === 0) options.ringer.stop();
    publish();
  };

  const ring = (call: IncomingCall) => {
    dismiss(call.roomId);
    const timeout = setTimeout(() => dismiss(call.roomId), RING_TIMEOUT_MS);
    const poll = setInterval(() => {
      calls.roomStatus(call.roomId).then(
        (status) => {
          if (!status.live || status.sessionId !== call.sessionId) {
            dismiss(call.roomId);
            return;
          }
          // The call is still live for other invitees; this person may
          // have declined it from a different device, which never changes
          // `roomStatus` (the call can still ring everyone else).
          calls.invitation(call.roomId).then(
            (invited) => {
              if (invited.invited && invited.sessionId === call.sessionId && invited.decline) {
                dismiss(call.roomId);
              }
            },
            () => {},
          );
        },
        () => {},
      );
    }, END_POLL_MS);
    const timers = [() => clearTimeout(timeout), () => clearInterval(poll)];
    ringing.set(call.roomId, { call, timers });
    if (ringing.size === 1) options.ringer.start();
    publish();
    options.onRing?.(call);
  };

  const onNotice = (
    roomId: string,
    roomName: string,
    sessionId: string,
    event: Pick<ChatTimelineEvent, 'senderName'>,
  ) => {
    calls.roomStatus(roomId).then(
      (status) => {
        if (!live || !status.live || status.sessionId !== sessionId) return;
        ring({ roomId, roomName, sessionId, callerName: event.senderName });
      },
      () => {},
    );
  };

  const check = (roomId: string) => {
    if (!live) return;
    const room = chat.rooms().find((r) => r.id === roomId);
    if (!room || room.muted) return;
    for (const event of chat.timeline(roomId).events) {
      if (event.ts < startedAt || event.mine || event.deleted) continue;
      const sessionId = parseCallNotice(event.body);
      if (!sessionId || seen.has(sessionId)) continue;
      seen.add(sessionId);
      onNotice(roomId, room.name, sessionId, event);
    }
  };

  const watch = (roomId: string) => {
    if (stopByRoom.has(roomId)) return;
    stopByRoom.set(
      roomId,
      chat.onTimelineChanged(roomId, () => check(roomId)),
    );
    check(roomId);
  };

  const onJoined = (e: Event) => dismiss(String((e as CustomEvent).detail));
  window.addEventListener(JOINED_EVENT, onJoined);

  for (const room of chat.rooms()) watch(room.id);
  const stopRooms = chat.onRoomsChanged((rooms) => {
    for (const room of rooms) watch(room.id);
  });

  return {
    dismiss,
    stop() {
      live = false;
      stopRooms();
      for (const stop of stopByRoom.values()) stop();
      stopByRoom.clear();
      window.removeEventListener(JOINED_EVENT, onJoined);
      for (const roomId of [...ringing.keys()]) dismiss(roomId);
    },
  };
}
